import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCurrentBillingPlan } from "../services/index";
import {
  ONBOARDING_BILLING_PLAN,
  UPGRADE_BILLING_PLAN,
} from "../navigation/CONSTANTS";
import { ONBOARDING_COMPLETED } from "../utils/onboardingStepsUtils";

/*
 * Shared "does this shop have an active subscription" check.
 * Pulled out of NoActiveSubscriptionModal.jsx so RouterConfig can also use it
 * to skip mounting data-heavy pages (Forecasting, Account, etc.) while the
 * blocking modal is shown — otherwise those pages keep fetching data in the
 * background for a shop that just cancelled, even though the UI is hidden
 * behind the overlay.
 *
 * Returns true only once we've confirmed (via API) that onboarding is done
 * and the shop has no active plan — never true while the check is still
 * loading, so normal pages aren't blocked by this during a fresh page load.
 */
const useNoActiveSubscription = () => {
  const location = useLocation();
  const { isLoggedIn, onboardingStepsStatus } = useSelector((state) => state.auth);
  const { subscriptionRevision } = useSelector((state) => state.user);
  const [checkState, setCheckState] = useState("idle");
  const [planId, setPlanId] = useState(undefined);
  const seenRevision = useRef(subscriptionRevision);

  const returnedFromBilling = (() => {
    const params = new URLSearchParams(location.search);
    return (
      params.has("chargeId") ||
      params.has("charge_id") ||
      params.has("billingplan")
    );
  })();

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }
    /*
     * Cancel just succeeded. Treat the shop as unsubscribed immediately so
     * the blocking modal appears before the status refetch returns.
     * Example: revision 0 → 1 after POST /billingplan/cancel
     */
    if (seenRevision.current !== subscriptionRevision && subscriptionRevision > 0) {
      setPlanId(null);
      setCheckState("ready");
    }
    seenRevision.current = subscriptionRevision;
    let cancelled = false;
    getCurrentBillingPlan()
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setCheckState((current) => (current === "ready" ? current : "error"));
          return;
        }
        setPlanId(res.data?.id ?? null);
        setCheckState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setCheckState((current) => (current === "ready" ? current : "error"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, subscriptionRevision, returnedFromBilling]);

  const isPlanSelectionRoute =
    location.pathname === ONBOARDING_BILLING_PLAN ||
    location.pathname === UPGRADE_BILLING_PLAN;
  const wizardComplete = onboardingStepsStatus === ONBOARDING_COMPLETED;
  const hasNoSubscription = checkState === "ready" && planId == null;

  return (
    isLoggedIn &&
    wizardComplete &&
    hasNoSubscription &&
    !isPlanSelectionRoute
  );
};

export default useNoActiveSubscription;
